import {
  DependencyList,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHandler } from "./hook";

export interface LoadState<T> {
  error?: any;
  pending: boolean;
  value?: T;
}

export function areLoadsResolved(loads: LoadState<unknown>[]): {
  allResolved: boolean;
  hasError: boolean;
} {
  const allResolved = loads.every(
    (load) =>
      !load.pending && (load.value !== undefined || load.error !== undefined),
  );
  const hasError = loads.some((load) => load.error !== undefined);
  return { allResolved, hasError };
}

export function mapLoadState<T, U>(
  loadState: LoadState<T>,
  fn: (value: T | undefined) => U | undefined,
): LoadState<U> {
  return { ...loadState, value: fn(loadState.value) };
}

interface LoadIteratorState<T> {
  readonly error?: any;
  readonly pending: boolean;
  readonly value: readonly T[];
}

export type Loader<T> = (abort: AbortSignal) => Promise<T>;

export type LoaderWithInput<I, T> = (
  input: I,
  abort: AbortSignal,
) => Promise<T>;

class InvalidIteratorStateError extends Error {
  override name = "InvalidIteratorStateError";

  constructor() {
    super("Invalid iterator state");
  }
}

export function useLoad<T>(fn: Loader<T>, deps: DependencyList): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ pending: false });

  useEffect(() => {
    const abortController = new AbortController();
    setState((state) => ({ ...state, pending: true }));
    fn(abortController.signal).then(
      (value) => {
        if (abortController.signal.aborted) {
          return;
        }
        setState({ pending: false, value });
      },
      (error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setState({ pending: false, error });
      },
    );
    return () => {
      abortController.abort();
      setState((state) => ({ ...state, pending: false }));
    };
    // The way useLoad() is designed, we have no choice but to trust that the user gave us the correct deps for fn().
    // We could fix this by marking useLoad() as a custom hook, and then exhaustive-deps would enforce that for us.
    // https://www.npmjs.com/package/eslint-plugin-react-hooks#advanced-configuration
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

export function useDebouncedLoad<T>(
  fn: Loader<T>,
  delay: number,
  deps: DependencyList,
  /** When false, nothing is scheduled and the state stays as it is. */
  enabled = true,
): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ pending: false });
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const abortController = new AbortController();
    setState((state) => ({ ...state, pending: true }));
    const timeout = setTimeout(() => {
      fn(abortController.signal).then(
        (value) => {
          if (abortController.signal.aborted) {
            return;
          }
          setState({ pending: false, value });
        },
        (error) => {
          if (abortController.signal.aborted) {
            return;
          }
          setState({ pending: false, error });
        },
      );
    }, delay);
    return () => {
      abortController.abort();
      clearTimeout(timeout);
      setState((state) => ({ ...state, pending: false }));
    };
    // The way useLoad() is designed, we have no choice but to trust that the user gave us the correct deps for fn().
    // We could fix this by marking useLoad() as a custom hook, and then exhaustive-deps would enforce that for us.
    // https://www.npmjs.com/package/eslint-plugin-react-hooks#advanced-configuration
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return state;
}

type IteratorNext<T> = (value?: T) => void;

/**
 * Fire-and-forget: the trigger returns before the work starts. Use
 * {@link useTriggerLoadAsync} when the caller must know whether it succeeded.
 */
export function useTriggerLoad<T>(
  fn: Loader<T>,
): [LoadState<T | undefined>, (signal?: AbortSignal) => void] {
  const [trigger, setTrigger] = useState<symbol | null>(null);

  const cleanup = useRef<() => void>(() => {});

  useEffect(() => {
    return () => cleanup.current();
  }, []);

  const triggerResolve = useRef<() => void>(() => {});
  const triggerFn = useHandler((signal?: AbortSignal) => {
    cleanup.current();
    setTrigger(Symbol());

    if (signal) {
      const stop = () => setTrigger(null);
      signal.addEventListener("abort", stop);
      cleanup.current = () => signal.removeEventListener("abort", stop);
    } else {
      cleanup.current = () => {};
    }
    void new Promise<void>((resolve) => {
      triggerResolve.current = resolve;
    });
  });

  const load = useLoad(
    async (abort) => {
      if (!trigger) {
        return undefined;
      }
      const resolve = triggerResolve.current;
      try {
        return await fn(abort);
      } finally {
        resolve();
      }
    },
    [trigger],
  );

  return [load, triggerFn];
}

/**
 * {@link useTriggerLoad} whose trigger returns a promise settling when the work
 * does — rejecting if it throws — so callers can tell success from failure.
 */
export function useTriggerLoadAsync<A extends unknown[], T>(
  fn: (...args: A) => Promise<T>,
): [LoadState<T | undefined>, (...args: A) => Promise<T>] {
  const [state, setState] = useState<LoadState<T | undefined>>({
    pending: false,
  });

  const trigger = useHandler(async (...args: A) => {
    setState((state) => ({ ...state, pending: true }));
    try {
      const value = await fn(...args);
      setState({ pending: false, value });
      return value;
    } catch (error) {
      setState({ pending: false, error });
      throw error;
    }
  });

  return [state, trigger];
}

export function useLoader<T>(): [
  LoadState<T | undefined>,
  (loader: Loader<T>) => void,
] {
  const ref = useRef<Loader<T> | undefined>();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [load, trigger] = useTriggerLoad((abort) => ref.current!(abort));

  const fn = useHandler((loader: Loader<T>) => {
    ref.current = loader;
    trigger();
  });

  return [load, fn];
}

export function useTriggerLoadWithInput<I, T>(
  fn: LoaderWithInput<I, T>,
): [LoadState<T | undefined>, (input: I, signal?: AbortSignal) => void] {
  const [triggerWithInput, setTriggerWithInput] = useState<{ input: I } | null>(
    null,
  );

  const cleanup = useRef<() => void>(() => {});

  useEffect(() => {
    return () => cleanup.current();
  }, []);

  const triggerResolve = useRef<() => void>(() => {});
  const triggerFn = useHandler((input: I, signal?: AbortSignal) => {
    cleanup.current();
    setTriggerWithInput({ input });

    if (signal) {
      const stop = () => setTriggerWithInput(null);
      signal.addEventListener("abort", stop);
      cleanup.current = () => signal.removeEventListener("abort", stop);
    } else {
      cleanup.current = () => {};
    }
    void new Promise<void>((resolve) => {
      triggerResolve.current = resolve;
    });
  });

  const load = useLoad(
    async (abort) => {
      if (!triggerWithInput) {
        return undefined;
      }
      const resolve = triggerResolve.current;
      try {
        return await fn(triggerWithInput.input, abort);
      } finally {
        resolve();
      }
    },
    [triggerWithInput],
  );

  return [load, triggerFn];
}

// TODO Remove? Not actually used anywhere
// @ts-expect-error unused-export-debt
function useLoadIterable<T, N = void>(
  iterable: AsyncIterable<T> | null,
  deps: DependencyList,
): [
  LoadIteratorState<T>,
  IteratorNext<N> | undefined,
  (newState: LoadIteratorState<T>) => void,
] {
  const [iterator, setIterator] = useState<AsyncIterator<T> | null>(null);

  const [state, setState] = useState<LoadIteratorState<T>>({
    pending: false,
    value: [],
  });

  const ready = useRef(false);

  useEffect(() => {
    ready.current = true;
    setState({ pending: false, value: [] });
    if (!iterable) {
      return;
    }
    const iterator = iterable[Symbol.asyncIterator]();
    setIterator(iterator);
    return () => {
      void iterator.return?.();
    };
    // The way useLoadIterable() is designed, we have no choice but to trust that the user gave us the correct deps.
    // We could fix this by marking useLoadIterable() as a custom hook, and then exhaustive-deps would enforce that for us.
    // https://www.npmjs.com/package/eslint-plugin-react-hooks#advanced-configuration
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, iterable]);

  const next = useCallback<IteratorNext<N>>(
    (value) => {
      if (!ready.current) {
        throw new InvalidIteratorStateError();
      }
      setState((state) => ({ ...state, pending: true }));
      ready.current = false;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      iterator!
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        .next(value as any)
        .then(({ value, done }) => {
          if (done) {
            setState((state) => ({ ...state, pending: false }));
            return;
          }
          setState((state) => ({
            pending: false,
            value: [...state.value, value],
          }));
          ready.current = true;
        })
        .catch((error) => {
          if (
            error.code !== "ERR_CANCELED" ||
            error.message !== "Another request is in flight"
          ) {
            setState((state) => ({ ...state, pending: false, error }));
          }
        });
    },
    [iterator],
  );

  const overwriteState = (newState: LoadIteratorState<T>) => {
    setState(newState);
  };

  return [state, ready.current && iterator ? next : undefined, overwriteState];
}
